/*
 * Copyright 2025 iodyne.  All rights reserved.
 * Use is subject to license terms.
 */

import { AuthStates, getAuthState } from "@/lib/auth/auth_state";
import type { ModelError } from "@/lib/models/errors";
import { ImageBuildAttrs, uniqueIDForFW, type FirmwareImageMetadata, type FirmwareImageURL } from "@/lib/models/firmware";
import { createEntityAdapter, createSelector, type EntityState } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { responseForAPIError } from "@/lib/datastore/slices/helpers";
import { translate as t } from "@/i18n/client_nohook";

// Constants
enum FirmwareAPIErrors {
  accessDeniedError = "AccessDeniedError",
  firmwareImageDNEError = "FirmwareImageDNEError",
  firmwareDownloadUnavailableError = "FirmwareDownloadUnvailableError",
  retryableFirmwareDownloadError = "retryableFirmwareDownloadError",
  invalidFilenameError = "InvalidFilenameError",
  invalidExtraMetadataError = "InvalidExtraMetadataError",
  tagStabilityClassMismatchError = "TagStabilityClassMismatchError",
  invalidResourceTargetError = "InvalidResourceTargetError",
  invalidSubjectTargetError = "InvalidSubjectTargetError",
  aclDNEError = "ACLDNEError",
  invalidIOFWFileError = "InvalidIOFWFileError",
  invalidIOFWMetadataError = "InvalidIOFWMetadataError",
  invalidIOFWSignatureError = "InvalidIOFWSignatureError"
}

// Slices
//@ts-ignore
const fwImageNamesAdapter = createEntityAdapter<string>({
  selectId: (str: string) => str
})
const fwImageNamesInitialState = fwImageNamesAdapter.getInitialState();

const fwImagesAdapter = createEntityAdapter<FirmwareImageMetadata[], string>({
  selectId: (e:FirmwareImageMetadata[]) => e[0].image_name
});
const fwImagesInitialState = fwImagesAdapter.getInitialState();

const fwImageURLsAdapter = createEntityAdapter({
  selectId: (e:FirmwareImageURL) => e.image_id
})
const fwImageURLsInitialState = fwImageURLsAdapter.getInitialState();

export const firmwareSlice = createApi({
  reducerPath: "firmware_api",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.IOD_CLOUD_PORTAL_FIRMWARE_API_URI}/v1/firmware/`,
    prepareHeaders: (headers, { getState }) => {
      headers.set("Authorization", `Bearer ${getAuthState(AuthStates.userAccessToken)}`);
      headers.set("X-Iodyne-Client-Feature-Level", "-1");
      return headers;
    }
  }),
  tagTypes: ["FWImage"],
  endpoints: build => ({
    // Retrieve Available Image Names (kinds)
    getImageNames: build.query<string[], {}>({
      query: () => "image_names",
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }): Promise<ModelError> {
        return responseForAPIError(resp, {});
      }
    }),

    //List Available Versions for Image Name
    getVersionsForImageName: build.query<EntityState<FirmwareImageMetadata[], string>, { image_name: string }>({
      query: ({ image_name }) => `image/${image_name}/versions`,
      transformResponse(resp: FirmwareImageMetadata[]) {
        return fwImagesAdapter.setOne(fwImagesInitialState, resp);
      },
      async transformErrorResponse(
        resp: { status: number, data: Record<string, any> }, _meta, { image_name }
      ): Promise<ModelError> {
        return responseForAPIError(
          resp,
          {
            [FirmwareAPIErrors.firmwareImageDNEError]: await t(
              "fw_mgmt.api_errors.firmware_image_dne_error",
              { image_name: image_name }
            )
          }
        );
      }
    }),

    //Get URL for Firmware Image
    getURLForImage: build.query<EntityState<FirmwareImageURL, string>, { image_name: string, image_version: string }>({
      query: ({ image_name, image_version }) => `image/${image_name}/version/${image_version}?no_redirect=true`,
      transformResponse(resp: FirmwareImageURL, _meta, { image_name, image_version }) {
        const [base_version, build_attrs] = image_version.split("+", 1);
        resp.image_id = uniqueIDForFW({
          image_name: image_name,
          image_version: base_version,
          image_build_attrs: build_attrs.split("").map((char) => ImageBuildAttrs[char])
        });

        return fwImageURLsAdapter.setOne(fwImageURLsInitialState, resp);
      },
      async transformErrorResponse(resp: { status: number, data: Record<string, any> }, _meta, { image_name, image_version }): Promise<ModelError> {
        const [base_version, build_attrs] = image_version.split("+", 1);
        return responseForAPIError(
          resp,
          {
            [FirmwareAPIErrors.firmwareImageDNEError]: await t(
              "fw_mgmt.api_errors.firmware_image_dne_error",
              {
                image_name: uniqueIDForFW({
                  image_name: image_name,
                  image_version: base_version,
                  image_build_attrs: build_attrs.split("").map((char) => ImageBuildAttrs[char])
                })
              }
            )
          }
        )
      }
    })
  })
});

export const {
  useGetImageNamesQuery,
  useGetVersionsForImageNameQuery,
  useGetURLForImageQuery
} = firmwareSlice;